import React, { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Users, BookOpen, FileText, ChevronDown, ChevronUp, Info } from "lucide-react"
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const ConferenceTracks: React.FC = () => {
    const { t } = useTranslation(['public', 'common']);
    const [expandedTracks, setExpandedTracks] = useState<{ [key: string]: boolean }>({});

    const toggleExpand = (id: string) => {
        setExpandedTracks((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const tracks = useMemo(() => [
        {
            id: "joint-hybrid-conference",
            title: "KEMENDIKTISAINTEK-BRIN-ISEAS-KONASPI XII UNDIKSHA JOINT CONFERENCE",
            subtitle: "“Education in Contemporary Indonesia: Navigating Governance Transitions and Human Capital Impact”",
            shortName: "BRIN-ISEAS Joint Conference",
            logo: "/logo/kemendiktisaintek-brin-iseas-konaspi-xii-undiksha.png",
            description: t('public:tracks_section.items.joint_conference'),
            speakers: [
                {
                    name: "Dr. Ahmad Najib Burhani, MA",
                    title: "Director General of Science and Technology, MoHEST",
                    topic: "Steering Committee",
                },
                {
                    name: "Dr. Muhammad Najib Azca",
                    title: "Chairman of Research Organization for Social Sciences and Humanities, BRIN",
                    topic: "Steering Committee",
                },
                {
                    name: "Dr. Trina Fizzanty",
                    title: "Head of Research Center for Education, BRIN",
                    topic: "Steering Committee",
                },
                {
                    name: "Ms. Julia Lau, M.A., LL.M",
                    title: "Principal Fellow, ISEAS - Yusof Ishak Institute",
                    topic: "Steering Committee",
                },
            ],
            topics: [
                "1. Education as Social Policy: Welfare Interventions in the School System",
                "2. Equity and Excellence: Reconfiguring the School System",
                "3. Digitalization and the Politics of Education Technology",
                "4. Universities, Research, and the Developmental State",
                "5. Comparative Higher Education: Lessons Learned for Indonesia",
                "6. Power Dynamics of Educational Governance",
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "2.4 MB" },
                { name: "Extended Abstract Guidelines", type: "PDF", size: "1.2 MB" },
            ],
            deadline: "July 31st, 2026",
            fee: "Free Conference Fee",
            detailLink: "/conference/kemendiktisaintek-brin-iseas-konaspi-xii-undiksha",
            submitLink: "https://s.brin.go.id/m/joint_hybrid_conference",
            registerLink: "https://s.brin.go.id/m/joint_hybrid_conference",
        },
        {
            id: "iconmns",
            title: "International Conference on Mathematics and Natural Sciences",
            shortName: "IConMNS",
            logo: "/conference-tracks/logo-iconms.jpg",
            description: t('public:tracks_section.items.iconmns'),
            speakers: [
                {
                    name: "-",
                    title: "-",
                    topic: "-",
                },
            ],
            topics: [
                "Mathematics", "Physics", "Biology", "Chemistry", "Astronomy", "Earth Science", "Limnology", "Fisheries", "Marine Science", "STEM", "Statistics", "Mathematics Education", "Phsyics Education", "Biology Education", "Chemistry Education", "Science Education", "Enviromental Sciences", "STEM Education", "Ethnomathematics", "Ethnosciences",
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "2.1 MB" },
                { name: "Template Paper", type: "DOCX", size: "1.5 MB" },
            ],
            deadline: "2026",
            fee: "Rp 0",
            submitLink: "https://fmipa.undiksha.ac.id/iconmns2026/",
            registerLink: "https://fmipa.undiksha.ac.id/iconmns2026/",
        },
        {
            id: "ictes",
            title: "The International Conference on Technology and Education Science",
            shortName: "ICTES",
            logo: "/conference-tracks/logo-ictes.png",
            description: t('public:tracks_section.items.ictes'),
            speakers: [
                {
                    name: "-",
                    title: "-",
                    topic: "-",
                },
            ],
            topics: [
                "Early Childhood Education", "Primary Education", "Adult Education", "Mathematics and Science Education", "Guidance and Counseling", "Educational Technology", "Character Education", "Special Needs Education", "Technical and Vocational Education", "Language Education", "Social Science Education", "Educational Management", "Sport Science and Physical Education", "Education Based on Local Wisdom", "Global Issues in Education and Research", "Research and Development in Education", "Assessment in Psychology and Education", "Higher Education", "Educational Psychology", "Nonformal Education", "Philosophy of Education", "Other Areas in Education",
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "2.3 MB" },
                { name: "Submission Guidelines", type: "PDF", size: "1.8 MB" },
            ],
            deadline: "2026",
            fee: "Rp 0",
            submitLink: "https://conference.undiksha.ac.id/ictes/2026/",
            registerLink: "https://conference.undiksha.ac.id/ictes/2026/",
        },
        {
            id: "iclsse",
            title: "International Conference on Law, Social Sciences, and Education",
            shortName: "ICLSSE",
            logo: "/conference-tracks/logo-iclsse.png",
            description: t('public:tracks_section.items.iclsse'),
            speakers: [
                {
                    name: "-",
                    title: "-",
                    topic: "-",
                },
            ],
            topics: [
                "Governing the Intangible", "Global Environmental Justice", "Restorative Justice in the Modern Era.", "Reimagining the Past and Future Society", "Digital Citizenship and Cultural Preservation", "Spatial Intelligence for a Sustainable Environment", "Redefining the Classroom", "Innovation Learning Ecosystems", "Knowledge Management & Pedagogy"
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "1.9 MB" },
                { name: "Abstract Template", type: "DOCX", size: "0.8 MB" },
            ],
            deadline: "2026",
            fee: "Rp 0",
            submitLink: "https://fhis.undiksha.ac.id/iclsse-2026/",
            registerLink: "https://fhis.undiksha.ac.id/iclsse-2026/",
        },
        {
            id: "teams",
            title: "Tourism, Economic, Accounting, Management and Social Science",
            shortName: "TEAMS",
            logo: "/conference-tracks/logo-teams.png",
            description: t('public:tracks_section.items.teams'),
            speakers: [
                {
                    name: "-",
                    title: "-",
                    topic: "-",
                },
            ],
            topics: [
                "Digital Economy & Business Innovation", "Sustainable Finance & Green Economy", "Management & Organizational Development", "Accounting & Financial Reporting", "Social Economy & Community Development", "Economics & Public Policy",
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "2.5 MB" },
                { name: "Registration Guide", type: "PDF", size: "1.2 MB" },
            ],
            deadline: "2026",
            fee: "Rp 0",
            submitLink: "https://fe.undiksha.ac.id/conference/teams2026/",
            registerLink: "https://fe.undiksha.ac.id/conference/teams2026/",
        },
        {
            id: "iconvet",
            title: "International Conference on Vocational Education and Technology",
            shortName: "IConVET",
            logo: "/conference-tracks/logo-iconvet.jpg",
            description: t('public:tracks_section.items.iconvet'),
            speakers: [
                {
                    name: "-",
                    title: "-",
                    topic: "-",
                },
            ],
            topics: [
                "Informatics Engineering", "Electronics", "Electricals", "Engineering Education", "Technology Inovation in Education", "Vocational Education and Training",
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "2.0 MB" },
                { name: "Presentation Template", type: "PPTX", size: "3.2 MB" },
            ],
            deadline: "2026",
            fee: "Rp 0",
            submitLink: "https://conference.undiksha.ac.id/iconvet/",
            registerLink: "https://conference.undiksha.ac.id/iconvet/",
        },
        {
            id: "iclaac",
            title: "International Conference on Languages and Arts Across Cultures",
            shortName: "ICLAAC",
            logo: "/conference-tracks/logo-iclaac.png",
            description: t('public:tracks_section.items.iclaac'),
            speakers: [
                {
                    name: "-",
                    title: "-",
                    topic: "-",
                },
            ],
            topics: [
                "Language Education, Pedagogy, and Linguistics", "Literature and Cultural Studies", "Arts, Design, and Creative Practices", "Interdisciplinary and Cross-Cultural Perspectives",
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "1.7 MB" },
                { name: "Author Guidelines", type: "PDF", size: "1.1 MB" },
            ],
            deadline: "15 Februari 2026",
            fee: "Rp 0",
            submitLink: "https://fbs.undiksha.ac.id/iclaac2026",
            registerLink: "https://fbs.undiksha.ac.id/iclaac2026",
        },
        {
            id: "icopesh",
            title: "International Conference on Physical Education, Sport and Health",
            shortName: "ICoPESH",
            logo: "/conference-tracks/logo-icopesh.png",
            description: t('public:tracks_section.items.icopesh'),
            speakers: [
                {
                    name: "-",
                    title: "-",
                    topic: "-",
                },
            ],
            topics: [
                "Sports Pedagogy", "Sports Psychology", "Sports Coaching", "Sports Medicine", "Sports Biomechanics", "Sports Management", "Sport Tourism", "Physical Education", "Public Health",
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "1.7 MB" },
                { name: "Author Guidelines", type: "PDF", size: "1.1 MB" },
            ],
            deadline: "15 Februari 2026",
            fee: "Rp 0",
            submitLink: "https://fok.undiksha.ac.id/4th-icopesh",
            registerLink: "https://fok.undiksha.ac.id/4th-icopesh",
        },
        {
            id: "gimhwelt",
            title: "Ganesha International Conference on Medical, Health, and Wellness Tourism",
            shortName: "GIMHWELT",
            logo: "/conference-tracks/logo-gimhwelt.png",
            description: t('public:tracks_section.items.gimhwelt'),
            speakers: [
                {
                    name: "-",
                    title: "-",
                    topic: "-",
                },
            ],
            topics: [
                "Herbal, Complementary, and Traditional Medicine", "Travel Health", "Sports Physiology and Wellness Psychology", "Interdisciplinary Approaches in Clinical and Health Sciences", "Public Health Policies and Practices", "Health Promotion and Education", "Artificial Intelligence (AI), Machine Learning, and Telemedicine in Healthcare", "Biomedical Science and Clinical Medicine"
            ],
            documents: [
                { name: "Call for Papers", type: "PDF", size: "1.7 MB" },
                { name: "Author Guidelines", type: "PDF", size: "1.1 MB" },
            ],
            deadline: "15 Februari 2026",
            fee: "Rp 0",
            submitLink: "https://fk.undiksha.ac.id/gimhwelt",
            registerLink: "https://fk.undiksha.ac.id/gimhwelt",
        },
    ], [t]);

    return (
        <section id="conference-tracks" className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-amber-600 bg-clip-text text-transparent">
                            {t('public:tracks_section.title')}
                        </h2>
                        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                            {t('public:tracks_section.subtitle')}
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-8">
                        {tracks.map((track, index) => {
                            const expanded = expandedTracks[track.id];
                            const visibleTopics = expanded ? track.topics : track.topics.slice(0, 6);

                            return (
                                <Card key={track.id} className={`hover:shadow-xl transition-all duration-300 group overflow-hidden flex flex-col ${track.id === 'joint-hybrid-conference' ? 'lg:col-span-2 border-2 border-amber-500/50 shadow-lg bg-gradient-to-br from-amber-50/40 via-white to-blue-50/40 dark:from-amber-950/20 dark:via-gray-900 dark:to-blue-950/20' : ''}`}>
                                    <CardHeader className={`pb-3 pt-3 ${track.id === 'joint-hybrid-conference' ? 'bg-gradient-to-r from-amber-500/10 via-blue-500/10 to-transparent dark:from-amber-500/15 dark:via-blue-500/15' : 'bg-gradient-to-r from-blue-50 to-amber-50 dark:from-blue-950/20 dark:to-amber-950/20 pb-2'}`}>
                                        <div className="flex items-start gap-4">
                                            <div className="w-20 h-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform p-2">
                                                <img
                                                    src={track.logo || "/placeholder.svg"}
                                                    alt={`${track.shortName} Logo`}
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center flex-wrap gap-2 mb-2">
                                                    <Badge variant={track.id === 'joint-hybrid-conference' ? "default" : "secondary"} className={`text-xs font-semibold ${track.id === 'joint-hybrid-conference' ? 'bg-gradient-to-r from-blue-600 to-amber-600 text-white border-0' : ''}`}>
                                                        {track.id === 'joint-hybrid-conference' ? 'Featured • Joint Hybrid Conference' : `Track ${index}`}
                                                    </Badge>
                                                    <Badge variant="outline" className="text-xs font-medium">
                                                        {track.shortName}
                                                    </Badge>
                                                    {track.id === 'joint-hybrid-conference' && (
                                                        <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 font-semibold">
                                                            Free Conference Fee
                                                        </Badge>
                                                    )}
                                                </div>
                                                <CardTitle className="text-lg md:text-xl font-bold text-primary dark:text-blue-400 leading-tight mb-1">
                                                    {track.title}
                                                </CardTitle>
                                                {(track as any).subtitle && (
                                                    <div className="text-sm md:text-base font-semibold text-amber-700 dark:text-amber-400 italic mt-1">
                                                        {(track as any).subtitle}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </CardHeader>

                                    <CardContent className="p-6 flex flex-col h-full space-y-6 pt-4 flex-grow">
                                        <CardDescription className="text-sm md:text-base text-muted-foreground dark:text-gray-300 leading-relaxed">
                                            {track.description}
                                        </CardDescription>

                                        {track.id === 'joint-hybrid-conference' && (
                                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-blue-500/5 dark:bg-blue-500/10 rounded-xl border border-blue-500/15 text-xs text-muted-foreground">
                                                <div>
                                                    <span className="font-semibold text-foreground block mb-1 text-sm">Organized By:</span>
                                                    MoHEST • BRIN • ISEAS Yusof Ishak Institute • KONASPI XII Undiksha
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-foreground block mb-1 text-sm">Steering Committee:</span>
                                                    Dr. Ahmad Najib Burhani, MA • Dr. Muhammad Najib Azca • Dr. Trina Fizzanty • Ms. Julia Lau, M.A., LL.M
                                                </div>
                                                <div>
                                                    <span className="font-semibold text-foreground block mb-1 text-sm">Important Dates & Venue:</span>
                                                    Abstract Submission: July 1st - 31st, 2026<br />
                                                    Conference Date: October 14 - 15th, 2026<br />
                                                    Venue: Widya Graha Building, BRIN Jakarta (Hybrid)
                                                </div>
                                            </div>
                                        )}

                                        {/* Topics */}
                                        <div>
                                            <h4 className="font-semibold text-sm text-foreground dark:text-white mb-3 flex items-center gap-2">
                                                <BookOpen className="w-4 h-4 text-primary" />
                                                Main Topics / Track
                                            </h4>
                                            <div className="flex flex-wrap gap-2 mb-3">
                                                {visibleTopics.map((topic, idx) => (
                                                    <Badge key={idx} variant="outline" className="text-xs">
                                                        {topic}
                                                    </Badge>
                                                ))}
                                            </div>
                                            {track.topics.length > 4 && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-xs text-primary flex items-center gap-1"
                                                    onClick={() => toggleExpand(track.id)}
                                                >
                                                    {expanded ? (
                                                        <>
                                                            {t('common:buttons.show_less')} <ChevronUp className="w-3 h-3" />
                                                        </>
                                                    ) : (
                                                        <>
                                                            {t('common:buttons.show_more')} <ChevronDown className="w-3 h-3" />
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </div>

                                        {/* Submission Info */}
                                        <div className="mt-auto bg-gradient-to-r from-primary/5 to-accent/5 dark:from-blue-500/10 dark:to-purple-500/10 rounded-lg p-4 border border-primary/20 dark:border-blue-500/30">
                                            <div className="flex flex-wrap sm:flex-nowrap gap-2">
                                                {(track as any).detailLink && (
                                                    <Link
                                                        to={(track as any).detailLink}
                                                        className="flex-1 inline-flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-slate-950 text-sm font-bold rounded-md px-4 py-2 shadow transition-colors"
                                                    >
                                                        <Info className="w-4 h-4 mr-2" />
                                                        Detail
                                                    </Link>
                                                )}
                                                <a
                                                    href={track.submitLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex-1 inline-flex items-center justify-center bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-md px-4 py-2 transition-colors"
                                                >
                                                    <FileText className="w-4 h-4 mr-2" />
                                                    {t('common:buttons.submit_paper')}
                                                </a>
                                                <a
                                                    href={track.registerLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex-1 inline-flex items-center justify-center bg-secondary hover:bg-secondary/90 text-secondary-foreground text-sm font-medium rounded-md px-4 py-2 transition-colors"
                                                >
                                                    <Users className="w-4 h-4 mr-2" />
                                                    {t('common:buttons.register')}
                                                </a>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );

};

export default ConferenceTracks;